import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import Masonry from 'react-masonry-css';
import {
  FaHeart,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaClock,
  FaUsers,
  FaCheck,
  FaSpinner,
  FaTimes,
  FaInfoCircle,
  FaConciergeBell,
  FaCameraRetro,
  FaEnvelopeOpenText,
  FaFacebookF,
  FaTwitter,
  FaInstagram,
  FaWhatsapp,
  FaPhoneAlt,
  FaGift,
  FaPalette,
  FaSave,
  FaChevronRight,
  FaChevronLeft
} from 'react-icons/fa';
import './WeddingWebsite.css';
import logo from '../../images/logo.png';
import { eventThemeApi } from '../../api/themes/eventThemeApi';
import { eventApi } from '../../api/events';
import { toast } from 'react-toastify';

// ── Safely try to load auth context (not available on public pages) ──
let useAuthContextSafe;
try {
  const ctx = require('../../context/AuthContext');
  useAuthContextSafe = ctx.useAuthContext;
} catch {
  useAuthContextSafe = null;
}

const WeddingWebsite = () => {
  const location = useLocation();
  const { slug } = useParams();

  const [activeSection, setActiveSection] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', amount: '' });
  const [formErrors, setFormErrors] = useState({ name: false, phone: false, amount: false });

  // ── Theme state ──
  const [themeColors, setThemeColors] = useState({
    primary: '#4a85ff',
    secondary: '#0666e0',
    tertiary: '#f8a5c2',
  });
  const [savedTheme, setSavedTheme] = useState(null);

  // ── Customize panel state ──
  const [showCustomizePanel, setShowCustomizePanel] = useState(false);
  const [customWebsites, setCustomWebsites] = useState([]);
  const [selectedWebsiteId, setSelectedWebsiteId] = useState(null);
  const [savingTheme, setSavingTheme] = useState(false);
  const [draftColors, setDraftColors] = useState({ ...themeColors });

  // ── Auth context (only inside AuthProvider) ──
  let authCtx = null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    authCtx = useAuthContextSafe ? useAuthContextSafe() : null;
  } catch {
    authCtx = null;
  }
  const isAuthenticated = authCtx?.isAuthenticated || false;
  const currentEvent = authCtx?.currentEvent || null;

  // ── Parse event from URL state ──
  const getEventData = () => {
    const params = new URLSearchParams(location.search);
    const stateData = params.get('state');
    return stateData ? JSON.parse(decodeURIComponent(stateData)) : null;
  };
  const eventData = getEventData();

  // ── Fetch public event by ID / slug ──
  const [liveEvent, setLiveEvent] = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(false);

  useEffect(() => {
    if (slug) {
      setLoadingEvent(true);
      (async () => {
        try {
          let resp;
          const isNumericId = /^\d+$/.test(slug);
          if (isNumericId) {
            resp = await eventApi.getEventById(slug);
          } else {
            resp = await eventApi.getEvent({ full_website_url: slug });
          }
          const payload = resp?.data ?? resp ?? {};
          const event = payload?.data ?? payload;
          if (event) {
            setLiveEvent(event);
            if (event.theme) {
              setThemeColors({
                primary: event.theme.primary_color || '#4a85ff',
                secondary: event.theme.secondary_color || '#0666e0',
                tertiary: event.theme.tertially_color || '#f8a5c2',
              });
            }
          }
        } catch (e) {
          console.error('Error fetching public event:', e);
        } finally {
          setLoadingEvent(false);
        }
      })();
    }
  }, [slug]);

  const activeEvent = liveEvent || eventData?.event;

  // ── Check if this logged-in user owns this event website ──
  const isOwner = isAuthenticated &&
    currentEvent &&
    activeEvent &&
    (String(currentEvent.id) === String(activeEvent.id) ||
      currentEvent.event_address === activeEvent.event_address);

  // ── Load real theme from API if owner ──
  useEffect(() => {
    if (isOwner) {
      eventThemeApi.saveThemeSettings && // ping to ensure module loaded
        eventThemeApi.getEventTheme &&
        (async () => {
          try {
            // Use the event-themes list and find ours (getMyEventTheme approach)
            const resp = await eventThemeApi.getEventThemes();
            const themes = resp?.data?.data || resp?.data || [];
            const eventId = currentEvent?.id;
            const myTheme = Array.isArray(themes)
              ? themes.find(t => String(t.event_id) === String(eventId))
              : null;
            if (myTheme) {
              const colors = {
                primary: myTheme.primary_color || '#4a85ff',
                secondary: myTheme.secondary_color || '#0666e0',
                tertiary: myTheme.tertially_color || '#f8a5c2',
              };
              setThemeColors(colors);
              setDraftColors(colors);
              setSelectedWebsiteId(myTheme.custom_website_id || null);
              setSavedTheme(myTheme);
            }
          } catch (e) {
            console.warn('Could not load event theme:', e);
          }
        })();

      // Load custom website options
      (async () => {
        try {
          const resp = await eventThemeApi.getCustomWebsites();
          const sites = resp?.data?.data || resp?.data || [];
          setCustomWebsites(Array.isArray(sites) ? sites : []);
        } catch (e) {
          console.warn('Could not load custom websites:', e);
        }
      })();
    } else if (activeEvent) {
      // Use colors from URL state as defaults
      setThemeColors({
        primary: activeEvent.primaryColor || activeEvent.theme?.primary_color || '#4a85ff',
        secondary: activeEvent.secondaryColor || activeEvent.theme?.secondary_color || '#0666e0',
        tertiary: activeEvent.tertiaryColor || activeEvent.theme?.tertially_color || '#f8a5c2',
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner]);

  // ── Countdown ──
  const calculateTimeRemaining = () => {
    if (!activeEvent?.event_date) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    const diff = new Date(activeEvent.event_date).getTime() - Date.now();
    if (diff > 0) {
      return {
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      };
    }
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  };
  const [timeRemaining, setTimeRemaining] = useState(calculateTimeRemaining());
  useEffect(() => {
    const timer = setInterval(() => setTimeRemaining(calculateTimeRemaining()), 1000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Ahadi Form ──
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormErrors(prev => ({ ...prev, [name]: false }));
  };

  const handleAhadiSubmit = (e) => {
    e.preventDefault();
    const errors = {
      name: !formData.name.trim(),
      phone: !formData.phone.trim(),
      amount: !formData.amount.trim() || isNaN(formData.amount),
    };
    setFormErrors(errors);
    if (!errors.name && !errors.phone && !errors.amount) {
      setSubmitting(true);
      setTimeout(() => {
        setSubmitting(false);
        setSubmitted(true);
        setFormData({ name: '', phone: '', amount: '' });
      }, 1500);
    }
  };

  // ── Theme save ──
  const handleSaveTheme = async () => {
    setSavingTheme(true);
    try {
      await eventThemeApi.saveThemeSettings({
        primary_color: draftColors.primary,
        secondary_color: draftColors.secondary,
        tertially_color: draftColors.tertiary,
        custom_website_id: selectedWebsiteId || null,
      });
      setThemeColors({ ...draftColors });
      toast.success('Theme saved successfully!');
      setShowCustomizePanel(false);
    } catch (e) {
      console.error(e);
      toast.error('Failed to save theme. Please try again.');
    } finally {
      setSavingTheme(false);
    }
  };

  // Active colors (use draft while panel is open for live preview)
  const activeColors = showCustomizePanel ? draftColors : themeColors;
  const primaryColor = activeColors.primary;
  const secondaryColor = activeColors.secondary;
  const tertiaryColor = activeColors.tertiary;

  if (loadingEvent && !activeEvent) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh', backgroundColor: '#faf9f6' }}>
        <div className="text-center">
          <FaSpinner className="fa-spin" size={48} color="#4a85ff" />
          <p className="mt-3 text-muted">Inapakia tovuti ya harusi...</p>
        </div>
      </div>
    );
  }

  if (!activeEvent) {
    return (
      <div className="wedding-error-page">
        <Container className="text-center py-5">
          <h1>Event Not Found</h1>
          <p>We couldn't find the wedding details you're looking for.</p>
          <Button variant="primary" href="/">Return Home</Button>
        </Container>
      </div>
    );
  }

  const event = activeEvent;

  const placeholders = {
    about: 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?auto=format&fit=crop&w=800&q=80',
    services: 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?auto=format&fit=crop&w=800&q=80',
    portfolio: 'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=800&q=80',
    contact: 'https://images.unsplash.com/photo-1520854221256-17451cc331bf?auto=format&fit=crop&w=800&q=80',
  };

  const sectionColors = {
    about: '#1C3144',
    services: '#D00000',
    portfolio: '#008148',
    contact: '#F6AA1C',
  };

  const getSectionImage = (section) => {
    const idx = { about: 0, services: 1, portfolio: 2, contact: 3 }[section];
    return event.wallpaper?.[idx]?.md_photo || event.wallpaper?.[idx]?.photo_path || placeholders[section];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Date TBD';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
  };

  const getLocationName = (loc) => {
    if (!loc) return '';
    if (typeof loc === 'string') return loc;
    return loc.display_name || loc.name || '';
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'about':
        return (
          <div className="content-inner w-100">
            <div className="text-center mb-5">
              <h1 className="page-title">The Story of <strong>Us</strong></h1>
              <hr className="hr-ornate" style={{ borderColor: primaryColor }} />
            </div>
            <div className="about-content mt-4 row align-items-center">
              <div className="col-lg-6 mb-4 mb-lg-0">
                <img src={getSectionImage('about')} alt="Our Story" className="img-fluid rounded shadow-sm"
                  style={{ maxHeight: '600px', width: '100%', objectFit: 'contain', backgroundColor: '#f8f9fa' }} />
              </div>
              <div className="col-lg-6 px-lg-4">
                <h4 className="mb-3 font-weight-bold" style={{ color: secondaryColor }}>To Infinity and Beyond!</h4>
                <p className="lead text-muted my-3">We met through mutual friends and instantly connected. Our journey has been filled with laughter, adventure, and endless love.</p>
                <p className="text-muted">We're excited to start this new chapter and celebrate with all of you. Thank you for being part of our special day. Your presence means the world to us.</p>
              </div>
            </div>
          </div>
        );
      case 'services':
        return (
          <div className="content-inner w-100">
            <div className="text-center mb-5">
              <h1 className="page-title">Event <strong>Details</strong></h1>
              <hr className="hr-ornate" style={{ borderColor: primaryColor }} />
            </div>
            <Row className="mt-5">
              <Col md={6} className="mb-4">
                <div className="detail-card h-100 text-center p-4 shadow-sm bg-white rounded" style={{ borderBottom: `4px solid ${primaryColor}` }}>
                  <FaCalendarAlt className="detail-icon mb-3" style={{ color: primaryColor, fontSize: '2rem' }} />
                  <h4 className="fw-bold">Ceremony</h4>
                  <p className="text-muted">{formatDate(event.event_date)}</p>
                  <p className="fw-bold"><FaClock className="me-2" style={{ color: secondaryColor }} /> 4:00 PM</p>
                  <p><FaMapMarkerAlt className="me-2" style={{ color: secondaryColor }} />{getLocationName(event.event_location_ibada) || 'Ceremony Venue'}</p>
                </div>
              </Col>
              <Col md={6} className="mb-4">
                <div className="detail-card h-100 text-center p-4 shadow-sm bg-white rounded" style={{ borderBottom: `4px solid ${primaryColor}` }}>
                  <FaUsers className="detail-icon mb-3" style={{ color: primaryColor, fontSize: '2rem' }} />
                  <h4 className="fw-bold">Reception</h4>
                  <p className="text-muted">{formatDate(event.event_date)}</p>
                  <p className="fw-bold"><FaClock className="me-2" style={{ color: secondaryColor }} /> 6:00 PM</p>
                  <p><FaMapMarkerAlt className="me-2" style={{ color: secondaryColor }} />{getLocationName(event.event_location_reception) || 'Reception Venue'}</p>
                </div>
              </Col>
            </Row>
            <div className="text-center mt-4">
              <div className="detail-card w-100 p-4 shadow-sm bg-white rounded">
                <FaHeart className="detail-icon mb-3" style={{ color: '#D00000', fontSize: '2rem' }} />
                <h4 className="fw-bold">Dress Code</h4>
                <p className="text-muted">Semi-Formal</p>
                <div className="d-flex justify-content-center align-items-center gap-3 mt-2">
                  <span className="d-flex align-items-center">
                    <span className="rounded-circle me-2" style={{ backgroundColor: primaryColor, width: '15px', height: '15px', display: 'inline-block' }}></span>
                    Primary
                  </span>
                  <span className="d-flex align-items-center">
                    <span className="rounded-circle me-2" style={{ backgroundColor: secondaryColor, width: '15px', height: '15px', display: 'inline-block' }}></span>
                    Secondary
                  </span>
                  <span className="d-flex align-items-center">
                    <span className="rounded-circle me-2" style={{ backgroundColor: tertiaryColor, width: '15px', height: '15px', display: 'inline-block' }}></span>
                    Accent
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      case 'portfolio':
        return (
          <div className="content-inner w-100">
            <div className="text-center mb-5">
              <h1 className="page-title">Our <strong>Gallery</strong></h1>
              <hr className="hr-ornate" style={{ borderColor: primaryColor }} />
            </div>
            <div className="gallery-masonry mt-4">
              {event.wallpaper && event.wallpaper.length > 0 ? (
                <Masonry breakpointCols={{ default: 3, 1100: 2, 700: 1 }} className="my-masonry-grid" columnClassName="my-masonry-grid_column">
                  {event.wallpaper.map((img, idx) => (
                    <div key={idx} className="gallery-item mb-4">
                      <img src={img.md_photo || img.photo_path} alt={`Gallery ${idx}`} className="img-fluid rounded shadow-sm w-100" />
                    </div>
                  ))}
                </Masonry>
              ) : (
                <p className="text-muted text-center w-100 mt-5">No photos available yet.</p>
              )}
            </div>
          </div>
        );
      case 'contact':
        return (
          <div className="content-inner w-100">
            <div className="text-center mb-5">
              <h1 className="page-title"><strong>Contact</strong> Us</h1>
              <hr className="hr-ornate" style={{ borderColor: primaryColor }} />
            </div>
            <div className="row justify-content-center mt-4 px-3">
              <div className="col-md-10">
                <p className="text-center text-muted lead mb-5">For any inquiries or directions, please feel free to reach out. We can't wait to celebrate with you!</p>
                <div className="contact-cards-container d-flex flex-column gap-4">
                  <div className="contact-card d-flex align-items-center p-4 rounded shadow-sm bg-white" style={{ borderLeft: `5px solid ${primaryColor}` }}>
                    <div className="icon-wrapper rounded-circle d-flex align-items-center justify-content-center me-4" style={{ width: '60px', height: '60px', backgroundColor: `${primaryColor}22`, color: primaryColor }}>
                      <FaPhoneAlt size={24} />
                    </div>
                    <div>
                      <h5 className="mb-1 font-weight-bold text-dark">Kamati ya Maandalizi</h5>
                      <p className="mb-0 text-muted">{event.mobile || '+255 123 456 789'}</p>
                    </div>
                  </div>
                  <div className="contact-card d-flex align-items-center p-4 rounded shadow-sm bg-white" style={{ borderLeft: `5px solid ${secondaryColor}` }}>
                    <div className="icon-wrapper rounded-circle d-flex align-items-center justify-content-center me-4" style={{ width: '60px', height: '60px', backgroundColor: `${secondaryColor}22`, color: secondaryColor }}>
                      <FaEnvelopeOpenText size={24} />
                    </div>
                    <div>
                      <h5 className="mb-1 font-weight-bold text-dark">Email</h5>
                      <p className="mb-0 text-muted">wedding@harusi.online</p>
                    </div>
                  </div>
                  <div className="social-links-card mt-4 p-4 rounded shadow-sm bg-light text-center" style={{ borderTop: `3px solid ${primaryColor}` }}>
                    <h6 className="fw-bold mb-3 text-muted text-uppercase">Tufuate Mitandaoni</h6>
                    <ul className="list-inline mb-0 d-flex justify-content-center gap-3">
                      <li><a href="#" className="btn btn-outline-primary rounded-circle d-flex align-items-center justify-content-center" style={{ width: '45px', height: '45px', borderColor: primaryColor, color: primaryColor }}><FaFacebookF /></a></li>
                      <li><a href="#" className="btn btn-outline-primary rounded-circle d-flex align-items-center justify-content-center" style={{ width: '45px', height: '45px', borderColor: primaryColor, color: primaryColor }}><FaTwitter /></a></li>
                      <li><a href="https://www.instagram.com/harusionline/" target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary rounded-circle d-flex align-items-center justify-content-center" style={{ width: '45px', height: '45px', borderColor: primaryColor, color: primaryColor }}><FaInstagram /></a></li>
                      <li><a href="https://wa.me/255747636516" target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary rounded-circle d-flex align-items-center justify-content-center" style={{ width: '45px', height: '45px', borderColor: primaryColor, color: primaryColor }}><FaWhatsapp /></a></li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="contact-footer mt-5 mb-4 text-center">
              <p className="text-muted small mb-0 px-3" style={{ fontSize: '0.8rem', lineHeight: '1.5' }}>
                © {new Date().getFullYear()} Harusi online Tanzania. All rights reserved.<br />
                Developed by <a href="https://www.sj.co.tz/" target="_blank" rel="noopener noreferrer" style={{ color: '#0666e0', textDecoration: 'none', fontWeight: 'bold' }}>SJ Software &amp; Consultancy</a>
              </p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="boots-wedding-wrapper" style={{ '--primary-color': primaryColor, '--secondary-color': secondaryColor }}>

      {/* ── Active Section Overlay ── */}
      {activeSection && (
        <div className="active-section-overlay position-fixed top-0 start-0 w-100 h-100 z-3 d-flex bg-white fade-in">
          <div className="active-content-area col-12 col-lg-9 h-100 overflow-y-auto pb-5 p-lg-5 pt-5 px-3">
            {renderContent()}
          </div>
          <div className="active-sidebar-area col-lg-3 d-none d-lg-flex position-relative cursor-pointer"
            onClick={() => setActiveSection(null)}
            style={{ backgroundImage: `url(${getSectionImage(activeSection)})`, backgroundSize: 'cover', backgroundPosition: 'center', cursor: 'pointer' }}>
            <div className="sidebar-overlay" style={{ backgroundColor: sectionColors[activeSection], opacity: 0.85, position: 'absolute', inset: 0 }}></div>
            <button className="bg-transparent border-0 position-absolute top-0 end-0 m-4 text-white z-2" style={{ fontSize: '2rem', cursor: 'pointer' }}><FaTimes /></button>
            <div className="sidebar-title-wrapper d-flex align-items-center justify-content-center w-100 h-100 z-2 position-relative pt-5 pb-5">
              <h1 className="text-white text-uppercase m-0" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                {activeSection === 'about' ? 'About Us' : activeSection === 'services' ? 'Event Details' : activeSection === 'portfolio' ? 'Gallery' : 'Contacts'}
              </h1>
            </div>
          </div>
          <button className="d-lg-none position-fixed top-0 end-0 m-3 bg-light rounded-circle border-0 p-2 z-3 shadow-sm d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }} onClick={() => setActiveSection(null)}><FaTimes /></button>
        </div>
      )}

      <main className="d-flex flex-column flex-lg-row min-vh-100">

        {/* ── Left Column ── */}
        <div className="left-panel d-flex flex-column justify-content-start align-items-center position-relative w-100 w-lg-50 bg-white">
          <div className="left-content w-100 text-center py-3 px-3 d-flex flex-column h-100 overflow-y-auto">
            <div className="logo-area mb-2">
              <img src={logo} alt="Harusi Online Logo" style={{ height: '40px', objectFit: 'contain' }} className="mb-2" />
              <h1 className="couple-names-brand mb-0 fw-bold" style={{ color: primaryColor, fontSize: '2rem' }}>
                {event.event_title || `${event.bride_name} & ${event.groom_name}`}
              </h1>
              <p className="text-muted mt-1 text-uppercase small fw-bold" style={{ fontSize: '0.75rem' }}>Karibu Kwenye Sherehe</p>
            </div>

            <div className="countdown-minimal mb-2">
              <p className="mb-2 text-dark fw-bold"><FaCalendarAlt className="me-2" style={{ color: primaryColor }} />{formatDate(event.event_date)}</p>
              <div className="d-flex justify-content-center gap-3">
                {[['days', 'Days'], ['hours', 'Hrs'], ['minutes', 'Min'], ['seconds', 'Sec']].map(([key, lbl], i) => (
                  <React.Fragment key={key}>
                    <div className="time-block">
                      <span className="time-val">{String(timeRemaining[key]).padStart(2, '0')}</span>
                      <span className="time-lbl">{lbl}</span>
                    </div>
                    {i < 3 && <span className="time-sep">:</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Ahadi Form */}
            <div className="inline-ahadi-form w-100 mt-2 px-lg-4 text-start">
              {submitted ? (
                <div className="text-center py-3 success-message">
                  <FaCheck className="success-icon mb-2 text-success" size={30} />
                  <h6 className="text-dark fw-bold">Asante!</h6>
                  <p className="text-muted small mb-0">Ahadi yako imepokelewa kikamilifu.</p>
                </div>
              ) : (
                <div className="p-2">
                  <h6 className="fw-bold text-dark mb-3 text-center d-flex align-items-center justify-content-center text-uppercase" style={{ fontSize: '0.8rem' }}>
                    <FaGift className="me-2" style={{ color: primaryColor }} /> Ahadi na Michango
                  </h6>
                  <Form onSubmit={handleAhadiSubmit} className="elegant-form">
                    <Form.Group className="mb-3">
                      <Form.Control type="text" name="name" value={formData.name} onChange={handleInputChange} isInvalid={formErrors.name} placeholder="Jina lako kamili" disabled={submitting} className="elegant-input" />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Control type="tel" name="phone" value={formData.phone} onChange={handleInputChange} isInvalid={formErrors.phone} placeholder="Namba ya simu" disabled={submitting} className="elegant-input" />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Control type="number" name="amount" value={formData.amount} onChange={handleInputChange} isInvalid={formErrors.amount} placeholder="Kiasi cha ahadi (TZS)" disabled={submitting} className="elegant-input" />
                    </Form.Group>
                    <Button type="submit" variant="primary" className="w-100 rounded-pill py-2 submit-btn fw-bold mt-2 border-0" disabled={submitting} style={{ backgroundColor: primaryColor }}>
                      {submitting ? <FaSpinner className="fa-spin" /> : 'Tuma Ahadi'}
                    </Button>
                  </Form>
                </div>
              )}
            </div>

            <div className="left-panel-footer mt-auto pt-3 pb-2 text-center w-100">
              <p className="text-muted small mb-0 px-3" style={{ fontSize: '0.7rem', lineHeight: '1.4' }}>
                © {new Date().getFullYear()} Harusi online Tanzania. All rights reserved.<br />
                Developed by <a href="https://www.sj.co.tz/" target="_blank" rel="noopener noreferrer" style={{ color: '#0666e0', textDecoration: 'none', fontWeight: 'bold' }}>SJ Software &amp; Consultancy</a>
              </p>
            </div>
          </div>
        </div>

        {/* ── Right Column Grid ── */}
        <div className="right-panel w-100 w-lg-50 position-relative">
          <div className="grid-nav row h-100 g-0">
            <div className="col-6 grid-item about-bg" onClick={() => setActiveSection('about')} style={{ backgroundImage: `url(${getSectionImage('about')})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
              <div className="grid-overlay" style={{ backgroundColor: sectionColors.about }}></div>
              <div className="grid-content"><FaInfoCircle className="grid-icon mb-3" /><h2 className="grid-title">Our Story</h2></div>
            </div>
            <div className="col-6 grid-item services-bg" onClick={() => setActiveSection('services')} style={{ backgroundImage: `url(${getSectionImage('services')})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
              <div className="grid-overlay" style={{ backgroundColor: sectionColors.services }}></div>
              <div className="grid-content"><FaConciergeBell className="grid-icon mb-3" /><h2 className="grid-title">Event Details</h2></div>
            </div>
            <div className="col-6 grid-item portfolio-bg" onClick={() => setActiveSection('portfolio')} style={{ backgroundImage: `url(${getSectionImage('portfolio')})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
              <div className="grid-overlay" style={{ backgroundColor: sectionColors.portfolio }}></div>
              <div className="grid-content"><FaCameraRetro className="grid-icon mb-3" /><h2 className="grid-title">Gallery</h2></div>
            </div>
            <div className="col-6 grid-item contact-bg" onClick={() => setActiveSection('contact')} style={{ backgroundImage: `url(${getSectionImage('contact')})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
              <div className="grid-overlay" style={{ backgroundColor: sectionColors.contact }}></div>
              <div className="grid-content"><FaPhoneAlt className="grid-icon mb-3" /><h2 className="grid-title">Contacts</h2></div>
            </div>
          </div>
        </div>
      </main>

      {/* ══════════════════════════════════════════════
          OWNER-ONLY: Floating Customize Button + Panel
          ══════════════════════════════════════════════ */}
      {isOwner && (
        <>
          {/* Floating trigger button */}
          {!showCustomizePanel && (
            <button
              onClick={() => setShowCustomizePanel(true)}
              title="Customize your website"
              style={{
                position: 'fixed', bottom: '24px', right: '24px', zIndex: 1000,
                background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                color: '#fff', border: 'none', borderRadius: '50px',
                padding: '12px 20px', fontWeight: '700', fontSize: '0.9rem',
                boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
                display: 'flex', alignItems: 'center', gap: '8px',
                cursor: 'pointer', transition: 'transform 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
            >
              <FaPalette /> Customize
            </button>
          )}

          {/* Slide-out Customize Panel */}
          {showCustomizePanel && (
            <div style={{
              position: 'fixed', top: 0, right: 0, bottom: 0, width: '320px',
              background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
              zIndex: 2000, display: 'flex', flexDirection: 'column',
              overflowY: 'auto',
            }}>
              {/* Panel Header */}
              <div style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`, padding: '20px', color: '#fff' }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0 fw-bold"><FaPalette className="me-2" /> Customize Website</h5>
                    <small style={{ opacity: 0.85 }}>Changes apply in real-time preview</small>
                  </div>
                  <button onClick={() => { setShowCustomizePanel(false); setDraftColors({ ...themeColors }); }}
                    style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: '36px', height: '36px', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FaTimes />
                  </button>
                </div>
              </div>

              {/* Panel Body */}
              <div style={{ padding: '20px', flex: 1 }}>

                {/* Color Pickers */}
                <div className="mb-4">
                  <h6 className="fw-bold mb-3" style={{ color: '#333', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '1px' }}>🎨 Theme Colors</h6>

                  {[
                    { label: 'Primary Color', key: 'primary' },
                    { label: 'Secondary Color', key: 'secondary' },
                    { label: 'Accent / Tertiary Color', key: 'tertiary' },
                  ].map(({ label, key }) => (
                    <div key={key} className="mb-3">
                      <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#555', display: 'block', marginBottom: '6px' }}>{label}</label>
                      <div className="d-flex align-items-center gap-2">
                        <input
                          type="color"
                          value={draftColors[key]}
                          onChange={e => setDraftColors(prev => ({ ...prev, [key]: e.target.value }))}
                          style={{ width: '48px', height: '36px', padding: '2px', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer' }}
                        />
                        <input
                          type="text"
                          value={draftColors[key]}
                          onChange={e => setDraftColors(prev => ({ ...prev, [key]: e.target.value }))}
                          style={{ flex: 1, padding: '6px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem', fontFamily: 'monospace' }}
                        />
                        <span style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: draftColors[key], border: '2px solid #eee', flexShrink: 0 }}></span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Custom Website selector */}
                {customWebsites.length > 0 && (
                  <div className="mb-4">
                    <h6 className="fw-bold mb-3" style={{ color: '#333', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '1px' }}>🌐 Website Layout</h6>
                    <Form.Select
                      value={selectedWebsiteId || ''}
                      onChange={e => setSelectedWebsiteId(e.target.value ? parseInt(e.target.value) : null)}
                      style={{ borderRadius: '8px', fontSize: '0.9rem' }}
                    >
                      <option value="">Default Layout</option>
                      {customWebsites.map(site => (
                        <option key={site.id} value={site.id}>{site.name || site.title || `Layout ${site.id}`}</option>
                      ))}
                    </Form.Select>
                  </div>
                )}

                {/* Live Color Preview */}
                <div className="mb-4 p-3 rounded" style={{ background: '#f8f9fa', border: '1px solid #eee' }}>
                  <p style={{ fontSize: '0.75rem', fontWeight: '600', color: '#888', marginBottom: '8px', textTransform: 'uppercase' }}>Preview</p>
                  <div className="d-flex gap-2 align-items-center">
                    <div style={{ flex: 1, height: '32px', borderRadius: '6px', background: `linear-gradient(135deg, ${draftColors.primary}, ${draftColors.secondary})` }}></div>
                    <div style={{ width: '32px', height: '32px', borderRadius: '6px', background: draftColors.tertiary }}></div>
                  </div>
                </div>
              </div>

              {/* Panel Footer */}
              <div style={{ padding: '16px 20px', borderTop: '1px solid #eee', background: '#fafafa' }}>
                <button
                  onClick={handleSaveTheme}
                  disabled={savingTheme}
                  style={{
                    width: '100%', padding: '12px', borderRadius: '8px', border: 'none',
                    background: `linear-gradient(135deg, ${draftColors.primary}, ${draftColors.secondary})`,
                    color: '#fff', fontWeight: '700', fontSize: '0.95rem', cursor: savingTheme ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    opacity: savingTheme ? 0.7 : 1,
                  }}
                >
                  {savingTheme ? <><FaSpinner className="fa-spin" /> Saving...</> : <><FaSave /> Save Theme</>}
                </button>
                <p style={{ fontSize: '0.75rem', color: '#999', textAlign: 'center', marginTop: '8px', marginBottom: 0 }}>
                  Theme is saved to your event profile
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WeddingWebsite;